import { useMemo } from 'react'
import { scoringConfig, digestConfig } from '../../config'
import { GhostButton } from '../shared/GhostButton'
import { Ring } from '../shared/Ring'
import { MiniFitCard } from '../shared/MiniFitCard'
import { formatCurrency, formatTimestamp } from '../../utils/formatters'

const FIT_SCORE = 84
const READINESS_SCORE = 70

const founderNarrative = [
  {
    title: 'Operating cadence',
    body: 'Weekly investor note hits 91% open; product ship log updated every Friday.'
  },
  {
    title: 'Customer proof',
    body: '3 funded pilots live with vertical AI revenue leaders; 2 NPS 9/10 testimonials.'
  },
  {
    title: 'Go-to-market wedge',
    body: '“FSV insight” workflow drives net-new expansion paths inside RevOps suites.'
  }
]

const warmIntroGates = scoringConfig.readiness_gates

const signalBands = [
  { label: 'Connect Progress', value: 82, target: '≥ 80%' },
  { label: 'Digest CTR', value: 12, target: '≥ 9%' },
  { label: 'Compliance-ready', value: 100, target: 'Audit log live' }
]

export function InvestorFitPanel() {
  const warmIntroUnlocked = FIT_SCORE >= warmIntroGates.min_fit_for_warm_intro && READINESS_SCORE >= warmIntroGates.min_readiness_for_warm_intro
  const valuationWindow = useMemo(() => formatTimestamp(), [])

  return (
    <section id="signals" className="col-span-1 md:col-span-3">
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-[0_12px_50px_rgba(8,13,24,0.6)] lg:col-span-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-muted">Investor Fit</p>
              <h3 className="mt-1 text-2xl font-semibold text-ink">Profile: Vertical AI · SMB</h3>
              <p className="text-sm text-muted">Series A bar ≥ {formatCurrency(scoringConfig.seriesA_min_mrr)}/mo MRR</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className="rounded-full bg-surface-light px-3 py-1">Warm path ready</span>
              <span className="rounded-full bg-surface-light px-3 py-1">Stage: Seed-A</span>
            </div>
          </div>
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center">
            <div className="flex items-center gap-4">
              <Ring value={FIT_SCORE} />
              <div className="space-y-1 text-sm">
                <div className="text-muted">Fit score</div>
                <div className="text-xl font-semibold text-ink">{FIT_SCORE}</div>
                <div className="text-xs text-muted">Readiness {READINESS_SCORE} • Payback 13.5mo &gt; 12mo</div>
              </div>
            </div>
            <div className="flex-1 rounded-xl border border-border-light bg-surface-light p-4 text-sm text-muted">
              <p className="text-ink font-medium">Next best action</p>
              <p className="mt-1">Ship pricing cohort chart + seed partner replies before requesting warm intro.</p>
              <p className="mt-3 text-xs">{scoringConfig.ai_tooling_note}</p>
            </div>
          </div>
          {!warmIntroUnlocked && (
            <div className="mt-6 rounded-xl border border-border-light bg-[#241518] p-4 text-xs text-brand-alert">
              {warmIntroGates.ui_copy_locked} Fit {warmIntroGates.min_fit_for_warm_intro}+ &amp; Readiness {warmIntroGates.min_readiness_for_warm_intro}+ required. Current: Fit {FIT_SCORE}, Readiness {READINESS_SCORE}.
            </div>
          )}
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <MiniFitCard title="Vertical AI SMB" score={82} subtitle="Lead • $250–750k" sample />
            <MiniFitCard title="Profitable SaaS" score={79} subtitle="Follow • fast" sample />
            <MiniFitCard title="Data infra wedge" score={76} subtitle="Lead / Follow" sample />
          </div>
          <div className="mt-6 rounded-xl border border-border-light bg-surface-light p-4">
            <div className="text-xs uppercase tracking-wide text-muted">
              Founder / Team Weight ({(scoringConfig.weights.founder_team * 100).toFixed(0)}%)
            </div>
            <ul className="mt-3 space-y-2 text-xs text-muted">
              {founderNarrative.map((item) => (
                <li key={item.title}>
                  <span className="text-ink">{item.title}:</span> {item.body}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${warmIntroUnlocked ? 'bg-cta-gradient text-white hover:opacity-90' : 'border border-border-light text-brand-alert cursor-not-allowed opacity-70'}`}
              disabled={!warmIntroUnlocked}
            >
              Request warm intro
            </button>
            <GhostButton>Generate Positioning Brief</GhostButton>
            <GhostButton>Open Investor Map</GhostButton>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-[11px] text-muted">
            {scoringConfig.card_metadata.show_source_badges && <span>Sources: Stripe • GA4 • Founder Journal</span>}
            {scoringConfig.card_metadata.show_timestamp && <span>Valuation window {valuationWindow}</span>}
          </div>
          <div className="mt-2 text-[11px] text-muted">
            {scoringConfig.stage_ceiling_copy}
            <br />
            {scoringConfig.valuation_disclaimer}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-6 shadow-[0_12px_50px_rgba(8,13,24,0.6)] lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-muted">Signals</p>
              <h3 className="mt-1 text-lg font-semibold text-ink">Targets &amp; progress</h3>
            </div>
            <span className="rounded-md border border-border-light bg-surface-light px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted">
              Sample
            </span>
          </div>
          <div className="mt-5 space-y-4">
            {signalBands.map((signal) => (
              <div key={signal.label} className="space-y-2">
                <div className="flex items-center justify-between text-sm text-muted">
                  <span className="text-ink">{signal.label}</span>
                  <span>Target {signal.target}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-light">
                  <div
                    className="h-full rounded-full bg-brand-glow"
                    style={{ width: `${Math.min(signal.value, 100)}%` }}
                  />
                </div>
                <div className="text-right text-xs text-muted">{signal.value}% complete</div>
              </div>
            ))}
          </div>
          {digestConfig.weekly_top_rollup.enabled && (
            <div className="mt-6 rounded-xl border border-border-light bg-surface-light p-4 text-xs text-muted">
              <p className="text-ink font-medium">Digest opt-in</p>
              <p className="mt-1">{digestConfig.weekly_top_rollup.blurb}</p>
            </div>
          )}
          <div className="mt-6 text-[11px] text-muted">
            <span>Sources: PostHog • GA4 • Stripe</span>
            <br />
            <span>Updated {valuationWindow}</span>
          </div>
          <div className="mt-4">
            <GhostButton>Export Card</GhostButton>
          </div>
        </div>
      </div>
    </section>
  )
}
